import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { VerifyForm } from "../settings";
import { Button, Icon } from "../../common";
import {
  selectInvoice,
  selectStep,
  nextStep,
  MAX_STEPS,
} from "../../../store/reducers/invoice";
import { InvoiceStatus } from "../../../types/InvoiceTypes";
import { useGlobalLoading } from "../../../hooks/useGlobalLoading";
import { useDisplayInvoiceWalletSection } from "../../../hooks/useDisplayInvoiceWalletSection";
import { useTriggerToastOnRTKError } from "../../../hooks/useTriggerToastOnRTKError";
import { useTriggerToastOnRTKSuccess } from "../../../hooks/useTriggerToastOnRTKSuccess";
import { useValidateInvoiceAmountWithFirstKYCLevel } from "../../../hooks/useValidateInvoiceAmountWithFirstKYCLevel";
import { useIssueInvoiceMutation } from "../../../store/services/invoices";
import { useInvoiceCopyShareLink } from "../../../hooks/useInvoiceCopyShareLink";
import { useInvoiceCurrencies } from "../../../hooks/useInvoiceCurrencies";
import { formatAmount } from "../../../utils/numbers/money";
import {
  checkIssuerDetails,
  checkJobDetails,
  checkPayerDetails,
  checkWallet,
} from "../../../utils/prepareData/invoiceFilledStatus";
import { selectCurrentUser } from "../../../store/reducers/user";
import { ROUTES } from "../../../routes";

export const MainAction: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const step = useSelector(selectStep);
  const invoice = useSelector(selectInvoice);
  const currentUser = useSelector(selectCurrentUser);

  const copyShareLink = useInvoiceCopyShareLink();

  const [
    issueInvoice,
    {
      isLoading: isLoadingIssue,
      isSuccess: isSuccessIssue,
      isError: isErrorIssue,
      error: errorIssue,
    },
  ] = useIssueInvoiceMutation();

  useTriggerToastOnRTKSuccess(isSuccessIssue, "Invoice approved");
  useTriggerToastOnRTKError(isErrorIssue, errorIssue);
  useGlobalLoading(isLoadingIssue);
  const showWalletForm = useDisplayInvoiceWalletSection(invoice);
  const invoiceCurrencies = useInvoiceCurrencies(invoice);

  const { valid: validAmount } =
    useValidateInvoiceAmountWithFirstKYCLevel(invoice);

  const onClickNext = () => {
    const skipNext = step === 2 && !showWalletForm;
    dispatch(nextStep(skipNext));
  };

  if (!invoice) {
    return <></>;
  }

  const { requires_first_level_kyc } = currentUser;

  const { status, amount, user_is_payer, public_id } = invoice;

  if (status === InvoiceStatus.CANCELLED) {
    return <></>;
  }

  if (
    [
      InvoiceStatus.PENDING,
      InvoiceStatus.PROCESSING,
      InvoiceStatus.PENDING_REVIEW,
      InvoiceStatus.PAID,
    ].includes(status)
  ) {
    const onCopyShareLink = async () => {
      if (public_id) {
        copyShareLink(public_id);
      }
    };

    return (
      <Button
        block
        contentClassName="flex flex-row justify-between w-full"
        variant={Button.variant.PRIMARY}
        onClick={onCopyShareLink}
      >
        Share invoice
        <Icon icon="Link" size={24} disableFill />
      </Button>
    );
  }

  if (step < MAX_STEPS) {
    if (!validAmount && step === 2) {
      return <VerifyForm className="w-full" invoice={invoice} />;
    }

    const canContinue = (() => {
      switch (step) {
        case 1:
          const { valid: issuerStatus } = checkIssuerDetails(invoice);
          const { valid: payerStatus } = checkPayerDetails(invoice);
          return issuerStatus && payerStatus;

        case 2:
          return checkJobDetails(invoice);

        case 3:
          return checkWallet(invoice);

        default:
          return true;
      }
    })();

    return (
      <Button
        block
        disabled={!canContinue}
        onClick={onClickNext}
        variant={Button.variant.PRIMARY}
      >
        Next
      </Button>
    );
  }

  if (status === InvoiceStatus.DRAFT) {
    if (requires_first_level_kyc) {
      return <VerifyForm className="w-full" invoice={invoice} />;
    }

    const onApprove = async () => {
      if (invoice) {
        await issueInvoice(invoice.id);
      }
    };

    return (
      <div className="flex flex-col gap-4 w-full">
        <div className="theme-text-secondary text-sm font-medium text-center">
          You can't edit the invoice after approving it
        </div>

        <Button
          block
          contentClassName="flex flex-row w-full justify-evenly"
          variant={Button.variant.PRIMARY}
          onClick={onApprove}
        >
          Approve invoice on {invoiceCurrencies?.input?.symbol}{" "}
          {formatAmount(amount)}
          <Icon icon="Thumb-up" size={24} disableFill />
        </Button>

        <Button
          block
          className="py-0"
          contentClassName="flex flex-row justify-center w-full"
          variant={Button.variant.TERTIARY}
          onClick={() => {
            navigate(user_is_payer ? ROUTES.PAY : ROUTES.RECEIVE);
          }}
        >
          Back to dashboard
        </Button>
      </div>
    );
  }

  return <></>;
};
